// Sliding session: while the user is active, swap the JWT for a fresh one once
// half of its lifetime has passed (POST /api/auth/refresh). The tenant's
// "session timeout" setting then behaves as an inactivity timeout: an idle tab
// still expires, a working user is never thrown out mid-task.
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const ACTIVITY_EVENTS = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];
const CHECK_EVERY_MS = 60 * 1000;

// exp/iat (seconds) from a JWT without verifying it; null when unreadable.
export function readTokenTimes(token) {
  try {
    const part = token.split('.')[1];
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const { exp, iat } = JSON.parse(json);
    if (typeof exp !== 'number' || typeof iat !== 'number') return null;
    return { exp, iat };
  } catch {
    return null;
  }
}

// True once more than half of the token's lifetime is used up, but before it
// expires (an expired token can't be refreshed; the 401 handler takes over).
export function shouldRefresh(token, nowMs = Date.now()) {
  const times = token && readTokenTimes(token);
  if (!times) return false;
  const now = nowMs / 1000;
  return now < times.exp && now >= times.iat + (times.exp - times.iat) / 2;
}

let inFlight = null;

export function refreshIfNeeded(onToken) {
  const token = localStorage.getItem('token');
  if (inFlight || !shouldRefresh(token)) return inFlight;
  inFlight = axios
    .post(`${API_URL}/api/auth/refresh`, null, { headers: { Authorization: `Bearer ${token}` } })
    .then(({ data }) => {
      // Another tab may have logged out meanwhile: don't resurrect the session.
      if (data?.access_token && localStorage.getItem('token') === token) {
        localStorage.setItem('token', data.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
        if (onToken) onToken(data.access_token);
      }
    })
    .catch(() => { /* not fatal: the token stays valid until exp */ })
    .finally(() => { inFlight = null; });
  return inFlight;
}

// Watch for user activity and refresh at most once per CHECK_EVERY_MS.
// Returns a cleanup function.
export function startSessionKeepAlive(onToken) {
  let last = 0;
  const onActivity = () => {
    const now = Date.now();
    if (now - last < CHECK_EVERY_MS) return;
    last = now;
    refreshIfNeeded(onToken);
  };
  ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
  onActivity();
  return () => ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
}

// OFF-24: the login page explains why the user landed there. The flag lives in
// sessionStorage (this tab only); the login page reads it, then clears it.
const EXPIRED_FLAG = 'sessionExpired';

export function markSessionExpired() {
  try { sessionStorage.setItem(EXPIRED_FLAG, '1'); } catch { /* storage blocked */ }
}

export function wasSessionExpired() {
  try { return sessionStorage.getItem(EXPIRED_FLAG) === '1'; } catch { return false; }
}

export function clearSessionExpired() {
  try { sessionStorage.removeItem(EXPIRED_FLAG); } catch { /* storage blocked */ }
}

// True when the stored JWT has already expired (unreadable tokens count as valid:
// the server decides).
export function isTokenExpired(token, nowMs = Date.now()) {
  const times = token && readTokenTimes(token);
  return Boolean(times) && nowMs / 1000 >= times.exp;
}
